import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Star, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

/**
 * CourseReviewSection — Iter77 / Engagement.
 *
 * Three concentric jobs in one component:
 *  1. Render the path's aggregate rating + count badge (top of card).
 *  2. Prompt the current user to leave a review (gated: ≥3 modules
 *     generated server-side — handled by 400 response we trap).
 *  3. List recent reviews (5 at a time, "load more" appends).
 *
 * Star input is fully keyboard-navigable (arrow keys + Enter) for
 * accessibility — Hub gets a lot of visits from screen-reader users
 * per posthog logs.
 */
export default function CourseReviewSection({ pathId, pathGoal }) {
  const [summary, setSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!pathId) return;
    setLoading(true);
    try {
      const [agg, mine] = await Promise.all([
        api.get(`/path/${pathId}/reviews`),
        api.get(`/path/${pathId}/review/me`).catch(() => ({ data: { review: null } })),
      ]);
      setSummary(agg.data?.summary || null);
      setReviews(agg.data?.reviews || []);
      const me = mine.data?.review || null;
      setMyReview(me);
      if (me) {
        setRating(me.rating);
        setText(me.review_text || "");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [pathId]);

  const submit = async () => {
    if (rating < 1 || rating > 5) { toast.error("Pick a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/path/${pathId}/review`, {
        rating,
        review_text: text.trim() || null,
      });
      toast.success(myReview ? "Review updated" : "Thanks for the review!");
      setEditing(false);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Couldn't save review");
    } finally {
      setSubmitting(false);
    }
  };

  const avg = summary?.avg ?? null;
  const count = summary?.count || 0;
  const dist = summary?.distribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

  return (
    <section
      className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden"
      data-testid="course-reviews"
    >
      <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 grid place-items-center rounded-xl bg-[#FFD700]/12 border border-[#FFD700]/30 shrink-0">
            <Star size={14} className="text-[#FFD700]" fill="currentColor" />
          </div>
          <div>
            <div className="label-eyebrow text-[#FFD700] mb-0.5">Reviews</div>
            <div className="font-display text-lg text-white tracking-tight">
              {count === 0
                ? "No reviews yet"
                : <><span data-testid="reviews-avg">{avg?.toFixed(1)}</span> <span className="text-[#FFD700]">★</span> <span className="text-white/55 text-[13px]">· {count} review{count === 1 ? "" : "s"}</span></>}
            </div>
          </div>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[11.5px] btn-mint-outline px-3 py-1.5 rounded-full inline-flex items-center gap-1"
            data-testid="reviews-write-cta"
          >
            <Sparkles size={11} /> {myReview ? "Edit your review" : "Write a review"}
          </button>
        )}
      </div>

      {/* Distribution bars */}
      {count > 0 && (
        <div className="px-5 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 border-b border-white/5">
          {[5, 4, 3, 2, 1].map((n) => {
            const c = dist[n] || 0;
            const pct = count ? Math.round((c / count) * 100) : 0;
            return (
              <div key={n} className="flex items-center gap-2 text-[11px] text-white/55">
                <span className="w-3 tabular-nums text-right">{n}</span>
                <Star size={9} className="text-[#FFD700]" fill="currentColor" />
                <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#FFD700] to-[#FFB067]"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-8 text-right font-mono-oak text-[10px] text-white/45">{c}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit form */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-b border-white/5"
          >
            <div className="px-5 py-4 space-y-3">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-display mb-1.5">Your rating</div>
                <div className="flex items-center gap-1.5" data-testid="reviews-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      className="p-1 rounded-md hover:bg-white/5"
                      aria-label={`Rate ${n} star${n === 1 ? "" : "s"}`}
                      data-testid={`reviews-star-${n}`}
                    >
                      <Star
                        size={22}
                        className={(hover || rating) >= n ? "text-[#FFD700]" : "text-white/20"}
                        fill={(hover || rating) >= n ? "currentColor" : "none"}
                      />
                    </button>
                  ))}
                  {rating > 0 && (
                    <span className="ml-2 text-[11.5px] text-white/55 font-mono-oak">
                      {["", "Not great", "Could be better", "Decent", "Good", "Excellent"][rating]}
                    </span>
                  )}
                </div>
              </div>
              <label className="block">
                <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-display mb-1.5">Your review (optional)</div>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value.slice(0, 1000))}
                  rows={3}
                  placeholder="What worked, what didn't. Helps other learners pick the right course."
                  className="w-full text-[13px] px-3 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 resize-none"
                  data-testid="reviews-text"
                />
                <div className="text-[10px] text-white/35 font-mono-oak mt-1 text-right">{text.length}/1000</div>
              </label>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => { setEditing(false); if (myReview) { setRating(myReview.rating); setText(myReview.review_text || ""); } else { setRating(0); setText(""); } }}
                  className="text-[12px] text-white/55 hover:text-white px-3 py-2 rounded-md hover:bg-white/5"
                  data-testid="reviews-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={submitting || rating < 1}
                  className="btn-primary px-4 py-2 rounded-full font-display font-semibold text-[12.5px] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  data-testid="reviews-submit"
                >
                  {submitting ? <><Loader2 size={11} className="animate-spin" /> Saving…</> : "Submit"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review list */}
      <div className="px-5 py-4 space-y-3">
        {loading && (
          <div className="text-white/45 text-xs flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading reviews…</div>
        )}
        {!loading && reviews.length === 0 && !editing && (
          <div className="text-white/45 text-[12.5px] text-center py-3">
            Be the first to review <span className="text-white">{pathGoal || "this course"}</span> — once you've generated 3 step whitepapers.
          </div>
        )}
        {reviews.map((r) => (
          <article key={r.review_id} className="rounded-lg border border-white/8 bg-white/[0.02] p-3" data-testid={`reviews-row-${r.review_id}`}>
            <div className="flex items-start gap-2.5">
              {r.user_picture ? (
                <img src={r.user_picture} alt="" className="h-7 w-7 rounded-full object-cover border border-white/10 shrink-0" />
              ) : (
                <div className="h-7 w-7 rounded-full bg-white/[0.06] border border-white/10 grid place-items-center text-[9px] font-display font-semibold text-white/65 shrink-0">
                  {(r.user_name || "?").slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-[12.5px] text-white truncate">{r.user_name || "Learner"}</div>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={10} className={n <= r.rating ? "text-[#FFD700]" : "text-white/15"} fill={n <= r.rating ? "currentColor" : "none"} />
                    ))}
                  </div>
                  <div className="text-[10px] text-white/35 font-mono-oak">{(r.created_at || "").slice(0, 10)}</div>
                </div>
                {r.review_text && (
                  <p className="text-[12.5px] text-white/75 leading-relaxed mt-1">{r.review_text}</p>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
